import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { parseDateLocal } from "@/lib/format";

type PubProject = {
  id: string;
  name: string;
  status: string;
  color: string | null;
  targetDate: string | null;
  createdAt: string;
};
type PubMilestone = {
  id: string;
  projectId: string;
  name: string;
  dueDate: string | null;
  taskCount: number;
  doneCount: number;
};

const DAY = 86_400_000;
const ROW_H = 40;
const LABEL_W = 180;

function dayNum(d: Date): number {
  return Math.floor(
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() / DAY,
  );
}

const STATUS_COLOR: Record<string, string> = {
  active: "#16a34a",
  on_hold: "#d97706",
  archived: "#6b7280",
};

/** Public, read-only roadmap rendered from a share token (no auth). */
function PublicRoadmap() {
  const { token } = Route.useParams();
  const q = useQuery({
    queryKey: ["public-roadmap", token],
    queryFn: async () => {
      const r = await fetch(`/api/public/roadmap/${token}`);
      if (!r.ok) throw new Error("not found");
      return (await r.json()) as {
        projects: PubProject[];
        milestones: PubMilestone[];
      };
    },
    retry: false,
  });

  const projects = (q.data?.projects ?? []).filter(
    (p) => p.status !== "archived",
  );
  const milestones = q.data?.milestones ?? [];
  const pxPerDay = 2;

  const { rangeStart, totalDays } = useMemo(() => {
    const nums: number[] = [];
    for (const p of projects) {
      const c = parseDateLocal(p.createdAt.slice(0, 10));
      if (c) nums.push(dayNum(c));
      if (p.targetDate) {
        const t = parseDateLocal(p.targetDate);
        if (t) nums.push(dayNum(t));
      }
    }
    const today = dayNum(new Date());
    nums.push(today - 30, today + 120);
    const min = Math.min(...nums) - 10;
    const max = Math.max(...nums) + 20;
    return { rangeStart: min, totalDays: max - min + 1 };
  }, [projects]);

  const xForDate = (s: string) =>
    (dayNum(parseDateLocal(s)!) - rangeStart) * pxPerDay;
  const width = totalDays * pxPerDay;
  const rowY = new Map(projects.map((p, i) => [p.id, i * ROW_H]));

  if (q.isLoading) return <div style={{ padding: 40 }}>Loading roadmap…</div>;
  if (q.isError)
    return (
      <div style={{ padding: 40 }}>
        <h1>Roadmap not found</h1>
        <p className="t-muted">
          This share link is invalid or has been revoked.
        </p>
      </div>
    );

  return (
    <div
      style={{ padding: "22px 30px 60px", maxWidth: 1200, margin: "0 auto" }}
    >
      <h1 className="pg-title" style={{ margin: "0 0 4px" }}>
        Roadmap
      </h1>
      <p className="t-sm t-muted" style={{ marginBottom: 16 }}>
        Read-only shared view.
      </p>
      <div className="tl-scroll">
        <div className="tl-grid" style={{ width: LABEL_W + width }}>
          <div className="tl-labels" style={{ width: LABEL_W }}>
            <div className="tl-corner" style={{ height: 28 }} />
            {projects.map((p) => (
              <div
                key={p.id}
                className="tl-row-label"
                style={{ height: ROW_H }}
              >
                {p.name}
              </div>
            ))}
          </div>
          <div className="tl-canvas" style={{ width }}>
            <div className="tl-header" style={{ height: 28, width }} />
            <div
              className="tl-body"
              style={{
                height: Math.max(projects.length * ROW_H, ROW_H),
                width,
              }}
            >
              {projects.map((p) => {
                const left = xForDate(p.createdAt.slice(0, 10));
                const endX = p.targetDate
                  ? xForDate(p.targetDate) + pxPerDay
                  : left + 21 * pxPerDay;
                return (
                  <div
                    key={p.id}
                    className="rm-bar"
                    style={{
                      top: (rowY.get(p.id) ?? 0) + 9,
                      left,
                      width: Math.max(12, endX - left),
                      background:
                        p.color ?? STATUS_COLOR[p.status] ?? "#2563eb",
                    }}
                    title={p.name}
                  >
                    <span className="rm-bar-label">{p.name}</span>
                  </div>
                );
              })}
              {milestones.map((m) => {
                if (!m.dueDate) return null;
                const y = rowY.get(m.projectId);
                if (y == null) return null;
                const done = m.taskCount > 0 && m.doneCount === m.taskCount;
                return (
                  <div
                    key={m.id}
                    className="rm-ms"
                    style={{
                      left: xForDate(m.dueDate),
                      top: y + 6,
                      color: done ? "#157049" : "#7e22ce",
                    }}
                    title={`${m.name} — ${m.dueDate}`}
                  >
                    ◆
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/r/$token")({
  component: PublicRoadmap,
});
